import React from 'react';
import PropTypes from 'prop-types';
import { Link, Navigate } from 'react-router-dom';
import ObjectTopBar from '../components/topbar/ObjectTopBar';
import DashboardCard from '../components/cards/DashboardCard';

/**
 * The landing page for a sidebar group whose submenu has nowhere to open.
 *
 * Collapsed to the 4rem rail there is no room for a submenu, and the first thing tried —
 * sending the group's one rail item straight to its first page — puts you on Forms when you
 * meant to reach API tokens, with no way to see that the others exist. This is the page that
 * list becomes: one card per child, which is also a better tap target than a 32px row.
 *
 * With one child there is nothing to choose, so it redirects rather than showing a page with a
 * single card on it. With none, the group should not have been reachable at all, so it sends the
 * user back to the dashboard rather than rendering an empty shell.
 *
 * Generic in `items` because the sidebar's groups are data (`navConfig`), not markup — today
 * Organization settings is the only group, and a second one should need no new page.
 */
export default function NavGroupLandingPage({ title, description, items, fallbackTo }) {
  if (items.length === 0) return <Navigate to={fallbackTo} replace />;
  if (items.length === 1) return <Navigate to={items[0].to} replace />;

  return (
    <>
      <ObjectTopBar title={title} />
      {/* `container py-4` is the documented page shell (AGENTS.md). It also has to be a
          container rather than `mx-2` because of the `.row` below: Bootstrap rows carry negative
          horizontal margins that a container's padding is there to absorb, and `mx-2` leaves
          them to overflow the page by a few pixels. */}
      <div className="container py-4">
        {description && (
          <p className="text-muted" style={{ maxWidth: 640 }}>{description}</p>
        )}
        <div className="row">
          {items.map(({ to, label, icon, description: itemDescription }) => (
            <DashboardCard key={to} className="col-12 col-sm-6 col-lg-4 mb-3" padding="p-0">
              {/* The whole card is the link, so the tap target is the card and not the words. */}
              <Link
                to={to}
                className="d-flex align-items-start gap-3 text-decoration-none text-reset p-4 h-100"
              >
                <i className={`bi ${icon} fs-4 text-primary`} aria-hidden />
                <span className="min-w-0">
                  <span className="d-block fw-semibold">{label}</span>
                  {itemDescription && (
                    <span className="d-block text-muted small mt-1">{itemDescription}</span>
                  )}
                </span>
                <i className="bi bi-chevron-right ms-auto text-muted flex-shrink-0" aria-hidden />
              </Link>
            </DashboardCard>
          ))}
        </div>
      </div>
    </>
  );
}

NavGroupLandingPage.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.shape({
    to: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    icon: PropTypes.string,
    description: PropTypes.string,
  })).isRequired,
  fallbackTo: PropTypes.string,
};

NavGroupLandingPage.defaultProps = {
  description: '',
  fallbackTo: '/admin/dashboard',
};
